/**
 * The token that proves a write came from this app rather than another site.
 *
 * The session cookie is `HttpOnly`; this one is deliberately readable, because
 * a write has to echo it back in a header. Another origin can make the browser
 * send our cookies but cannot read them, so it cannot produce the header.
 */

export const CSRF_HEADER = "X-CSRF-Token";

// The API adds the `__Host-` prefix wherever it can and drops it on plain-HTTP
// worktree stacks, where its required `Secure` flag would stop the cookie being
// stored at all. Both names are read; the prefixed one wins.
const COOKIE_NAMES = ["__Host-ps_csrf", "ps_csrf"] as const;

/** The current token, or null when there is no session. */
export function csrfToken(): string | null {
  for (const name of COOKIE_NAMES) {
    const match = document.cookie.match(
      new RegExp(
        `(?:^|;\\s*)${name.replace(/[.*+?^${}()|[\]\\-]/g, "\\$&")}=([^;]*)`,
      ),
    );
    if (match?.[1]) return decodeURIComponent(match[1]);
  }
  return null;
}

/** Whether this method changes state and therefore needs the proof. */
export function needsCsrf(method: string | undefined): boolean {
  const verb = (method ?? "GET").toUpperCase();
  return !["GET", "HEAD", "OPTIONS", "TRACE"].includes(verb);
}

/**
 * The headers for a request, with the proof attached when one is needed.
 *
 * Sends nothing when there is no token. The API refuses a cookie-bearing write
 * without one, which is correct: it means the session has gone.
 */
export function withCsrf(
  method: string | undefined,
  headers?: Record<string, string>,
): Record<string, string> {
  const result: Record<string, string> = { ...headers };
  if (!needsCsrf(method)) return result;

  const token = csrfToken();
  if (token) result[CSRF_HEADER] = token;
  return result;
}
